import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import Button from "@/shared/components/button";
import Field from "@/shared/components/field";
import { usePlans } from "../hooks/usePlans";
import type Plan from "../models/Plan";
import type PlanRule from "../models/PlanRule";
import Roles from "@/shared/valueObjects/Roles";
import GlassBackground from "@/shared/components/glassBackground";
import BackButton from "@/shared/components/backButton";

type PlanFormData = Omit<Plan, "id">;

type TimeUnitKey = "minutes" | "hours" | "days" | "months" | "years";

const TIME_UNIT_OPTIONS: { value: TimeUnitKey; label: string }[] = [
  { value: "minutes", label: "Minutos" },
  { value: "hours", label: "Horas" },
  { value: "days", label: "Días" },
  { value: "months", label: "Meses" },
  { value: "years", label: "Años" },
];

const RULE_TYPE_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Límite de usuarios" },
  { value: 1, label: "Acceso a recursos" },
  { value: 2, label: "Límite de almacenamiento" },
  { value: 3, label: "Activación de características" },
];

const DEFAULT_RULE: PlanRule = {
  name: "",
  description: "",
  key: "",
  type: 0,
  value: "",
};

const ROLE_OPTIONS = [
  { value: 0, label: "Super Administrador" },
  { value: 1, label: "Administrador" },
  { value: 2, label: "Cuenta Estándar" },
];

const DEFAULT_FORM: PlanFormData = {
  title: "",
  description: "",
  price: 0,
  deadline: new Date(),
  timeUnits: {
    minutes: 0,
    hours: 0,
    days: 0,
    months: 0,
    years: 1,
  },
  targetRole: Roles.isCuenta(2) ? 2 : 2,
  rules: [],
};

export default function PlanFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getPlan, createPlan, updatePlan, loading, error } = usePlans();

  const isEditing = !!id;

  const [formData, setFormData] = useState<PlanFormData>(DEFAULT_FORM);
  const [durationUnit, setDurationUnit] = useState<TimeUnitKey>("years");
  const [durationAmount, setDurationAmount] = useState(1);

  const buildTimeUnits = (amount: number, unit: TimeUnitKey) => ({
    minutes: unit === "minutes" ? amount : 0,
    hours: unit === "hours" ? amount : 0,
    days: unit === "days" ? amount : 0,
    months: unit === "months" ? amount : 0,
    years: unit === "years" ? amount : 0,
  });

  useEffect(() => {
    if (!isEditing) return;
    let ignore = false;
    const fetchPlan = async () => {
      const plan = await getPlan(id);
      if (!ignore && plan) {
        setFormData({
          title: plan.title ?? "",
          description: plan.description,
          price: plan.price,
          deadline: plan.deadline,
          timeUnits: plan.timeUnits,
          targetRole: plan.targetRole,
          rules: plan.rules ?? [],
        });
        const u = plan.timeUnits;
        const detected: TimeUnitKey =
          u.years > 0
            ? "years"
            : u.months > 0
              ? "months"
              : u.days > 0
                ? "days"
                : u.hours > 0
                  ? "hours"
                  : "minutes";
        setDurationUnit(detected);
        setDurationAmount(u[detected] || 1);
      }
    };
    fetchPlan();
    return () => { ignore = true; };
  }, [id, isEditing, getPlan]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    if (type === "date") {
      setFormData((prev) => ({ ...prev, [name]: value || null }));
      return;
    }
    const isNumeric = name === "price" || name === "targetRole";
    setFormData((prev) => ({
      ...prev,
      [name]: isNumeric ? Number(value) : value,
    }));
  };

  const handleTimeUnitChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    if (name === "durationUnit") {
      const unit = value as TimeUnitKey;
      setDurationUnit(unit);
      setFormData((prev) => ({
        ...prev,
        timeUnits: buildTimeUnits(durationAmount, unit),
      }));
    } else if (name === "durationAmount") {
      const amount = Math.max(1, Number(value));
      setDurationAmount(amount);
      setFormData((prev) => ({
        ...prev,
        timeUnits: buildTimeUnits(amount, durationUnit),
      }));
    }
  };

  const handleRuleChange = (
    index: number,
    field: keyof PlanRule,
    value: string | number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule,
      ),
    }));
  };

  const addRule = () => {
    setFormData((prev) => ({
      ...prev,
      rules: [...prev.rules, { ...DEFAULT_RULE }],
    }));
  };

  const removeRule = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing) {
      await updatePlan(id, formData);
    } else {
      await createPlan(formData);
    }
    navigate("/admin/plans");
  };

  return (
    <GlassBackground>
      <div className="relative flex-1 overflow-auto p-10">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <BackButton text={"Volver a planes"} link={"/admin/plans"}>
            <h2 className="text-[40px] font-bold tracking-tight text-foreground">
              {isEditing ? "Editar Plan" : "Nuevo Plan"}
            </h2>
            <p className="text-foreground/50 text-lg font-medium mt-1">
              {isEditing
                ? "Actualiza la información del plan de suscripción."
                : "Configura un nuevo plan de suscripción."}
            </p>
          </BackButton>

          {/* Form Card */}
          <div className="bg-white/60 border border-black/5 backdrop-blur-md rounded-4xl p-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)]">
            {isEditing && loading && (
              <div className="flex justify-center py-10">
                <Loader2 className="size-8 animate-spin text-accent" />
              </div>
            )}

            {error && (
              <p className="text-red-500 text-sm mb-6">
                Error: {error.message}
              </p>
            )}

            {(!isEditing || !loading) && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <Field
                  label="Título del plan"
                  name="title"
                  value={formData.title}
                  placeholder="Ej: Plan Básico"
                  required
                  onChange={handleChange}
                />

                <Field
                  label="Descripción"
                  name="description"
                  value={formData.description}
                  placeholder="Describe brevemente este plan..."
                  onChange={handleChange}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    label="Precio (US$)"
                    name="price"
                    type="number"
                    value={formData.price}
                    placeholder="0.00"
                    required
                    onChange={handleChange}
                  />

                  <Field
                    label="Fecha límite"
                    name="deadline"
                    type="date"
                    value={formData.deadline}
                    required
                    onChange={handleChange}
                  />
                </div>

                <Field
                  label="Rol objetivo"
                  name="targetRole"
                  type="select"
                  value={formData.targetRole as number}
                  onChange={handleChange}
                  options={ROLE_OPTIONS}
                />

                {/* Duration */}
                <div>
                  <p className="text-sm font-semibold text-foreground/60 uppercase tracking-widest mb-3">
                    Duración del plan
                  </p>
                  <div className="flex gap-3">
                    <Field
                      label="Cantidad"
                      name="durationAmount"
                      type="number"
                      value={durationAmount}
                      onChange={handleTimeUnitChange}
                    />
                    <Field
                      label="Unidad"
                      name="durationUnit"
                      type="select"
                      value={durationUnit}
                      onChange={handleTimeUnitChange}
                      options={TIME_UNIT_OPTIONS}
                    />
                  </div>
                </div>

                {/* Rules */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-foreground/60 uppercase tracking-widest">
                      Reglas del plan
                    </p>
                    <button
                      type="button"
                      onClick={addRule}
                      className="flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline transition-all"
                    >
                      <Plus className="size-4" />
                      Agregar regla
                    </button>
                  </div>

                  {formData.rules.length === 0 && (
                    <p className="text-sm text-foreground/30 italic">
                      No hay reglas configuradas.
                    </p>
                  )}

                  <div className="space-y-4">
                    {formData.rules.map((rule, i) => (
                      <div
                        key={i}
                        className="relative bg-black/3 border border-black/5 rounded-2xl p-5 space-y-4"
                      >
                        <button
                          type="button"
                          onClick={() => removeRule(i)}
                          className="absolute top-4 right-4 p-1.5 rounded-lg text-foreground/30 hover:text-red-500 hover:bg-red-50 transition-all"
                        >
                          <Trash2 className="size-4" />
                        </button>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Field
                            label="Nombre"
                            name={`rule-name-${i}`}
                            value={rule.name}
                            placeholder="Ej: Max usuarios"
                            onChange={(e) =>
                              handleRuleChange(i, "name", e.target.value)
                            }
                          />
                          <Field
                            label="Clave"
                            name={`rule-key-${i}`}
                            value={rule.key}
                            placeholder="Ej: max_users"
                            onChange={(e) =>
                              handleRuleChange(i, "key", e.target.value)
                            }
                          />
                        </div>

                        <Field
                          label="Descripción"
                          name={`rule-description-${i}`}
                          value={rule.description}
                          placeholder="Describe esta regla..."
                          onChange={(e) =>
                            handleRuleChange(i, "description", e.target.value)
                          }
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Field
                            label="Tipo"
                            name={`rule-type-${i}`}
                            type="select"
                            value={rule.type as number}
                            onChange={(e) =>
                              handleRuleChange(
                                i,
                                "type",
                                Number(e.target.value),
                              )
                            }
                            options={RULE_TYPE_OPTIONS}
                          />
                          <Field
                            label="Valor"
                            name={`rule-value-${i}`}
                            value={rule.value}
                            placeholder="Ej: 10"
                            onChange={(e) =>
                              handleRuleChange(i, "value", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/admin/plans")}
                  >
                    Cancelar
                  </Button>
                  <Button
                    submit
                    disabled={loading}
                    className="h-11 px-6 rounded-xl bg-accent hover:bg-accent/90 text-white border-none shadow-[0_0_20px_rgba(12,123,254,0.3)]"
                  >
                    {loading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Save className="size-4" />
                    )}
                    {isEditing ? "Guardar cambios" : "Crear plan"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </GlassBackground>
  );
}
